import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const MarketDataTable = ({ onSymbolSelect, selectedSymbol }) => {
  const [sortBy, setSortBy] = useState('volume');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  // Mock market data
  const mockMarketData = [
    {
      symbol: 'BTC/USD',
      name: 'Bitcoin',
      price: 43275.50,
      change24h: 2.45,
      volume24h: 28500000000,
      marketCap: 847000000000,
      high24h: 43890.25,
      low24h: 42150.75,
      exchange: 'Binance'
    },
    {
      symbol: 'ETH/USD',
      name: 'Ethereum',
      price: 2650.75,
      change24h: -1.23,
      volume24h: 15200000000,
      marketCap: 318000000000,
      high24h: 2720.50,
      low24h: 2580.25,
      exchange: 'Coinbase'
    },
    {
      symbol: 'ADA/USD',
      name: 'Cardano',
      price: 0.52,
      change24h: 4.67,
      volume24h: 890000000,
      marketCap: 18500000000,
      high24h: 0.54,
      low24h: 0.49,
      exchange: 'Kraken'
    },
    {
      symbol: 'SOL/USD',
      name: 'Solana',
      price: 98.45,
      change24h: -3.21,
      volume24h: 2100000000,
      marketCap: 42000000000,
      high24h: 102.75,
      low24h: 95.20,
      exchange: 'Binance'
    },
    {
      symbol: 'DOT/USD',
      name: 'Polkadot',
      price: 7.85,
      change24h: 1.89,
      volume24h: 450000000,
      marketCap: 9800000000,
      high24h: 8.12,
      low24h: 7.65,
      exchange: 'Coinbase'
    },
    {
      symbol: 'MATIC/USD',
      name: 'Polygon',
      price: 0.89,
      change24h: 6.34,
      volume24h: 680000000,
      marketCap: 8200000000,
      high24h: 0.92,
      low24h: 0.83,
      exchange: 'Kraken'
    }
  ];

  const filteredAndSortedData = useMemo(() => {
    let filtered = mockMarketData?.filter(item => {
      const matchesSearch = item?.symbol?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      if (filter === 'gainers') return matchesSearch && item?.change24h > 0;
      if (filter === 'losers') return matchesSearch && item?.change24h < 0;
      return matchesSearch;
    });

    return filtered?.sort((a, b) => {
      const aValue = a?.[sortBy];
      const bValue = b?.[sortBy];
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [searchTerm, filter, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const formatPrice = (price) => {
    if (price >= 1) return `$${price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${price?.toFixed(6)}`;
  };

  const formatVolume = (volume) => {
    if (volume >= 1e9) return `$${(volume / 1e9)?.toFixed(1)}B`;
    if (volume >= 1e6) return `$${(volume / 1e6)?.toFixed(1)}M`;
    return `$${(volume / 1e3)?.toFixed(1)}K`;
  };

  const formatMarketCap = (marketCap) => {
    if (marketCap >= 1e12) return `$${(marketCap / 1e12)?.toFixed(1)}T`;
    if (marketCap >= 1e9) return `$${(marketCap / 1e9)?.toFixed(1)}B`;
    return `$${(marketCap / 1e6)?.toFixed(1)}M`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
        <h3 className="text-lg font-semibold text-foreground">Market Data</h3>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
          <span className="text-sm text-muted-foreground">Live</span>
        </div>
      </div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search symbols..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            variant={filter === 'gainers' ? 'success' : 'outline'}
            size="sm"
            onClick={() => setFilter('gainers')}
          >
            <Icon name="TrendingUp" size={14} />
            Gainers
          </Button>
          <Button
            variant={filter === 'losers' ? 'destructive' : 'outline'}
            size="sm"
            onClick={() => setFilter('losers')}
          >
            <Icon name="TrendingDown" size={14} />
            Losers
          </Button>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('symbol')}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  Symbol
                  <Icon 
                    name={sortBy === 'symbol' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={12} 
                  />
                </Button>
              </th>
              <th className="text-right py-3 px-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('price')}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  Price
                  <Icon 
                    name={sortBy === 'price' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={12} 
                  />
                </Button>
              </th>
              <th className="text-right py-3 px-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('change24h')}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  24h Change
                  <Icon 
                    name={sortBy === 'change24h' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={12} 
                  />
                </Button>
              </th>
              <th className="text-right py-3 px-2 hidden md:table-cell">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('volume24h')}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  Volume
                  <Icon 
                    name={sortBy === 'volume24h' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={12} 
                  />
                </Button>
              </th>
              <th className="text-right py-3 px-2 hidden lg:table-cell">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('marketCap')}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  Market Cap
                  <Icon 
                    name={sortBy === 'marketCap' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={12} 
                  />
                </Button>
              </th>
              <th className="text-center py-3 px-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData?.map((item) => (
              <tr 
                key={item?.symbol}
                className={`border-b border-border hover:bg-muted/50 cursor-pointer transition-colors ${
                  selectedSymbol === item?.symbol ? 'bg-muted' : ''
                }`}
                onClick={() => onSymbolSelect(item?.symbol)}
              >
                <td className="py-3 px-2">
                  <div>
                    <div className="font-medium text-foreground text-sm">{item?.symbol}</div>
                    <div className="text-xs text-muted-foreground">{item?.name}</div>
                  </div>
                </td>
                <td className="py-3 px-2 text-right">
                  <div className="font-medium text-foreground text-sm">
                    {formatPrice(item?.price)}
                  </div>
                  <div className="text-xs text-muted-foreground">{item?.exchange}</div>
                </td>
                <td className="py-3 px-2 text-right">
                  <div className={`font-medium text-sm ${
                    item?.change24h >= 0 ? 'text-success' : 'text-error'
                  }`}>
                    {item?.change24h >= 0 ? '+' : ''}{item?.change24h?.toFixed(2)}%
                  </div>
                </td>
                <td className="py-3 px-2 text-right hidden md:table-cell">
                  <div className="font-medium text-foreground text-sm">
                    {formatVolume(item?.volume24h)}
                  </div>
                </td>
                <td className="py-3 px-2 text-right hidden lg:table-cell">
                  <div className="font-medium text-foreground text-sm">
                    {formatMarketCap(item?.marketCap)}
                  </div>
                </td>
                <td className="py-3 px-2 text-center">
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onSymbolSelect(item?.symbol);
                    }}
                  >
                    <Icon name="Plus" size={12} />
                    Trade
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredAndSortedData?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No symbols found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default MarketDataTable;